import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import RunInputForm from '@/pages/WorkflowsCatalog/components/run/run-input-form';
import RunJsonForm from '@/pages/WorkflowsCatalog/components/run/run-json-form';
import type { WorkflowDetail } from '@/pages/WorkflowsCatalog/types';
import { getDefaultValues } from '@/pages/WorkflowsCatalog/utils/schemaUtils';
import { History, Loader2 } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

interface SectionProps {
  workflow: WorkflowDetail;
}

export type FormValues = {
  workflow_name: string;
  jsonInput?: string;
  args?: Record<string, unknown>;
};

export const RunSection = ({ workflow }: SectionProps) => {
  // Parse URL params for rerunFrom
  const rerunFrom = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const rerunFromStr = params.get('rerunFrom');
    if (!rerunFromStr) return null;
    const [workflowId, runId] = rerunFromStr.split(':');
    return workflowId && runId ? { workflowId, runId } : null;
  }, []);

  const defaultValues = { args: getDefaultValues(workflow.inputSchema) };

  const form = useForm<FormValues>({
    defaultValues,
  });

  const [loadingLast, setLoadingLast] = useState(false);
  const [formKey, setFormKey] = useState(0);

  // Load input from previous run if rerunFrom is specified
  useEffect(() => {
    if (!rerunFrom) return;

    const loadPreviousInput = async () => {
      try {
        const res = await fetch(
          `/workflows/${rerunFrom.workflowId}/runs/${rerunFrom.runId}.json?provider=${workflow.provider}`,
          {
            headers: { Accept: 'application/json' },
          }
        );
        if (!res.ok) throw new Error('Failed to load previous run');
        const data = await res.json();
        const args = data.workflowInput?.args ?? data.workflowInput;
        if (!args || Object.keys(args).length === 0) {
          toast.info('Previous run input not available for this provider');
          return;
        }
        form.reset({ args, jsonInput: JSON.stringify(args, null, 2) });
        setFormKey((k) => k + 1);
        toast.info('Loaded input from previous run');
      } catch {
        toast.error('Failed to load previous run input');
      }
    };

    void loadPreviousInput();
  }, [rerunFrom, form]);

  const fetchJson = async <T,>(url: string) => {
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`Request failed: ${res.status}`);
    return (await res.json()) as T;
  };

  const handleLoadLast = async () => {
    try {
      setLoadingLast(true);

      const { workflows = [] } = await fetchJson<{ workflows: any[] }>(
        `/workflows?name=${encodeURIComponent(workflow.function_name)}&provider=${workflow.provider}&format=json`
      );
      if (!workflows.length) {
        toast.error('No previous runs: This workflow has no runs yet.');
        return;
      }

      const latest = workflows.reduce((a: any, b: any) => {
        const ta = new Date(a.startTime || 0).getTime();
        const tb = new Date(b.startTime || 0).getTime();
        return tb > ta ? b : a;
      });

      if (!latest?.id || !latest?.runId) throw new Error('Latest run missing identifiers');

      const { workflowInput } = await fetchJson<{ workflowInput?: any }>(
        `/workflows/${latest.id}/runs/${latest.runId}.json?provider=${workflow.provider}`
      );

      const args = workflowInput?.args ?? workflowInput ?? {};
      form.reset({ args, jsonInput: JSON.stringify(args ?? {}, null, 2) });
      setFormKey((k) => k + 1);

      toast.success('Loaded inputs from the latest run.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load last run inputs');
    } finally {
      setLoadingLast(false);
    }
  };

  return (
    <div className="animate-in fade-in-50 duration-200">
      <section>
        <h2 className="text-xl font-semibold mb-6">Execute Workflow</h2>
        <div className="p-6 rounded-lg border bg-card">
          <Tabs defaultValue="form" className="w-full">
            <div className="flex items-center justify-between mb-6">
              <TabsList className="w-fit">
                <TabsTrigger value="form">Form Input</TabsTrigger>
                <TabsTrigger value="json">Raw JSON</TabsTrigger>
              </TabsList>
              <Button variant="outline" size="sm" onClick={handleLoadLast} disabled={loadingLast}>
                {loadingLast ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <History className="mr-2 h-4 w-4" />
                )}
                Load last run inputs
              </Button>
            </div>
            <TabsContent value="form">
              <RunInputForm key={`form-${formKey}`} form={form} workflow={workflow} />
            </TabsContent>
            <TabsContent value="json">
              <RunJsonForm key={`json-${formKey}`} form={form} workflow={workflow} />
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};
